import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  Loader2, CalendarDays, Users, MessageSquare, ChevronRight, 
  Plus, Coffee, ExternalLink, Bell, Clock
} from "lucide-react";
import { Session } from "@supabase/supabase-js";
import { format, formatDistanceToNow, startOfDay, endOfDay, addDays } from "date-fns";

type ActivityItem = {
  id: string;
  type: 'booking' | 'notification' | 'message' | 'calendar';
  title: string;
  description: string;
  created_at: string;
  link?: string;
  metadata?: Record<string, any>;
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [session, setSession] = useState<Session | null>(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Fetch user profile
  const { data: userProfile } = useQuery({
    queryKey: ["user-profile", session?.user?.id],
    queryFn: async () => {
      if (!session?.user?.id) return null;
      const { data, error } = await supabase
        .from("profiles")
        .select("full_name")
        .eq("id", session.user.id)
        .single();
      if (error) return null;
      return data;
    },
    enabled: !!session?.user?.id,
  });

  // Fetch recent bookings (last 7 days)
  const { data: recentBookings, isLoading: bookingsLoading } = useQuery({
    queryKey: ["activity-recent-bookings"],
    queryFn: async () => {
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      
      const { data, error } = await supabase
        .from("coffeebeans_bookings")
        .select("id, booking_number, guest_name, experience_name, experience_date, guest_count, total_amount, created_at, status, brand_id, payment_method, agent_code_id")
        .is("deleted_at", null).eq("is_test", false)
        .gte("created_at", sevenDaysAgo.toISOString())
        .order("created_at", { ascending: false })
        .limit(10);

      if (error) throw error;
      
      // Fetch agent types for bookings with agent_code_id
      if (data) {
        const agentCodeIds = [...new Set(data.filter(b => b.agent_code_id).map(b => b.agent_code_id!))];
        if (agentCodeIds.length > 0) {
          const { data: agentCodes } = await supabase
            .from("agent_codes")
            .select("id, agent_type")
            .in("id", agentCodeIds);
          
          const agentTypeMap = new Map(agentCodes?.map(a => [a.id, a.agent_type]) || []);
          return data.map(b => ({
            ...b,
            _agent_type: b.agent_code_id ? agentTypeMap.get(b.agent_code_id) || null : null,
          }));
        }
      }
      
      return data?.map(b => ({ ...b, _agent_type: null as string | null })) || [];
    },
  });

  // Fetch dashboard notifications
  const { data: notifications, isLoading: notificationsLoading } = useQuery({
    queryKey: ["activity-notifications", session?.user?.id],
    queryFn: async () => {
      if (!session?.user?.id) return [];
      const { data, error } = await supabase
        .from("dashboard_notifications")
        .select("*")
        .eq("user_id", session.user.id)
        .order("created_at", { ascending: false })
        .limit(15);

      if (error) throw error;
      return data;
    },
    enabled: !!session?.user?.id,
  });

  // Fetch upcoming calendar events (next 7 days)
  const { data: upcomingEvents, isLoading: eventsLoading } = useQuery({
    queryKey: ["activity-upcoming-events"],
    queryFn: async () => {
      const now = new Date();
      const nextWeek = addDays(now, 7);
      
      const { data, error } = await supabase
        .from("calendar_events")
        .select("id, title, start_time, event_type, location")
        .gte("start_time", now.toISOString())
        .lte("start_time", nextWeek.toISOString())
        .order("start_time", { ascending: true })
        .limit(200);

      if (error) throw error;
      const groups = new Map<string, any>();
      for (const e of data ?? []) {
        const isBooking = e.event_type === "booking";
        const name = isBooking ? (e.title ?? "").split(" - ")[0].trim() : e.title;
        const key = isBooking ? `b|${name}|${e.start_time}` : `e|${e.id}`;
        const g = groups.get(key);
        if (g) g.count += 1;
        else groups.set(key, { ...e, title: isBooking ? name : e.title, originalTitle: e.title, count: 1 });
      }
      return Array.from(groups.values()).map((g) => (g.count === 1 ? { ...g, title: g.originalTitle } : g));
    },
  });

  // Fetch unread message count
  const { data: unreadCount } = useQuery({
    queryKey: ["activity-unread-messages", session?.user?.id],
    queryFn: async () => {
      if (!session?.user?.id) return 0;
      const { count, error } = await supabase
        .from("direct_messages")
        .select("*", { count: "exact", head: true })
        .eq("recipient_id", session.user.id)
        .is("read_at", null);

      if (error) return 0;
      return count || 0;
    },
    enabled: !!session?.user?.id,
  });

  const CREATIVE_TRAVEL_BRAND_ID = 'ef0ce1ff-c11a-4641-9fa4-e6d9c2883670';

  // Build unified activity feed
  const activityFeed: ActivityItem[] = [];

  // Add bookings to feed
  recentBookings?.forEach((booking) => {
    const isCreativeTravel = booking.brand_id === CREATIVE_TRAVEL_BRAND_ID;
    const link = isCreativeTravel
      ? `/operations/creative-travel?booking=${booking.id}`
      : `/operations/coffeebeans?booking=${booking.id}`;

    const isAgent = booking.payment_method === 'agent_invoice';
    const isConcierge = booking._agent_type === 'CONCIERGE';
    const title = isCreativeTravel 
      ? 'Creative Travel Booking' 
      : isConcierge 
        ? 'Concierge Booking' 
        : isAgent 
          ? 'Agent Booking' 
          : 'New Booking';

    activityFeed.push({
      id: `booking-${booking.id}`,
      type: 'booking',
      title,
      description: `${booking.guest_name} booked ${booking.experience_name} for ${booking.guest_count} guests on ${format(new Date(booking.experience_date), 'MMM d')}`,
      created_at: booking.created_at,
      link,
      metadata: { bookingNumber: booking.booking_number, status: booking.status, isAgent, isConcierge },
    });
  });

  // Add notifications to feed (excluding booking types to avoid duplicates)
  notifications?.filter(n => n.type !== 'booking').forEach((notif) => {
    // Generate proper link based on entity type
    let link: string | undefined;

    // Reminder-style alerts are informational only — not clickable
    const isReminderAlert = notif.type === 'alert' && (
      notif.title.startsWith('Invoice reminder:') ||
      notif.title.startsWith('Concierge payout due:')
    );

    if (!isReminderAlert && notif.related_entity_id && notif.related_entity_type) {
      switch (notif.related_entity_type) {
        case 'calendar_event':
          link = `/calendar?eventId=${notif.related_entity_id}`;
          break;
        case 'lead':
          link = /intake/i.test(notif.title || '')
            ? `/lead/${notif.related_entity_id}?tab=student-intake`
            : `/lead/${notif.related_entity_id}?tab=communications`;
          break;
        case 'student_intake_submission':
          link = `/intake-submission/${notif.related_entity_id}`;
          break;
        case 'channel_message':
          link = `/?openMessaging=true&channelId=${notif.related_entity_id}`;
          break;
        case 'client':
          link = `/clients?client=${notif.related_entity_id}`;
          break;
        case 'coffeebeans_booking':
        case 'booking':
          link = `/operations/coffeebeans?booking=${notif.related_entity_id}`;
          break;
        default:
          link = undefined;
      }
    }
    
    activityFeed.push({
      id: `notif-${notif.id}`,
      type: 'notification',
      title: notif.title,
      description: notif.message || '',
      created_at: notif.created_at,
      link,
      metadata: { isRead: notif.is_read, notificationType: notif.type },
    });
  });

  // Sort by created_at descending
  activityFeed.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

  const isLoading = bookingsLoading || notificationsLoading || eventsLoading;

  if (isLoading && !activityFeed.length) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const userName = userProfile?.full_name?.split(' ')[0] || 'there';
  const greeting = getGreeting();

  return (
    <div className="bg-background min-h-screen">
      {/* Header */}
      <div className="border-b bg-card">
        <div className="container mx-auto px-4 py-6">
          <h1 className="text-2xl font-bold text-foreground">
            {greeting}, {userName}!
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {format(new Date(), "EEEE, MMMM d, yyyy")}
          </p>
        </div>
      </div>

      <main className="container mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Activity Feed (2/3 width) */}
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">Recent Activity</CardTitle>
                  <Badge variant="secondary" className="font-normal">
                    Last 7 days
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <ScrollArea className="h-[500px]">
                  {activityFeed.length === 0 ? (
                    <div className="p-6 text-center text-muted-foreground">
                      <Bell className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p>No recent activity</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-border">
                      {activityFeed.slice(0, 20).map((item) => (
                        <div
                          key={item.id}
                          className={`p-4 hover:bg-muted/50 transition-colors ${item.link ? 'cursor-pointer' : ''}`}
                          onClick={async () => {
                            if (!item.link) return;
                            const m = item.link.match(/^\/intake-submission\/(.+)$/);
                            if (m) {
                              const { data: sub } = await (supabase as any).from("student_intake_submissions").select("lead_id").eq("id", m[1]).maybeSingle();
                              if (sub?.lead_id) navigate(`/lead/${sub.lead_id}?tab=student-intake&submissionId=${m[1]}`);
                              return;
                            }
                            navigate(item.link);
                          }}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`mt-1 p-2 rounded-full ${getActivityIconBg(item.type)}`}>
                              {getActivityIcon(item.type)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-sm">{item.title}</span>
                                {item.metadata?.isConcierge && (
                                  <Badge className="text-xs bg-amber-500/15 text-amber-700 border-amber-300 hover:bg-amber-500/20">
                                    Concierge
                                  </Badge>
                                )}
                                {item.metadata?.isAgent && !item.metadata?.isConcierge && (
                                  <Badge className="text-xs bg-teal-500/15 text-teal-700 border-teal-300 hover:bg-teal-500/20">
                                    Agent
                                  </Badge>
                                )}
                                {item.metadata?.bookingNumber && (
                                  <Badge variant="outline" className="text-xs">
                                    #{item.metadata.bookingNumber}
                                  </Badge>
                                )}
                              </div>
                              <p className="text-sm text-muted-foreground line-clamp-2 mt-0.5">
                                {item.description}
                              </p>
                              <p className="text-xs text-muted-foreground mt-1">
                                {formatDistanceToNow(new Date(item.created_at), { addSuffix: true })}
                              </p>
                            </div>
                            {item.link && (
                              <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-2" />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Stats & Quick Actions (1/3 width) */}
          <div className="space-y-6">
            {/* Quick Stats */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Quick Stats</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm">
                    <Coffee className="h-4 w-4 text-amber-600" />
                    <span>New Bookings (7d)</span>
                  </div>
                  <Badge variant="secondary">{recentBookings?.length || 0}</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm">
                    <MessageSquare className="h-4 w-4 text-blue-600" />
                    <span>Unread Messages</span>
                  </div>
                  <Badge variant={unreadCount && unreadCount > 0 ? "destructive" : "secondary"}>
                    {unreadCount || 0}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm">
                    <CalendarDays className="h-4 w-4 text-green-600" />
                    <span>Upcoming Events</span>
                  </div>
                  <Badge variant="secondary">{upcomingEvents?.length || 0}</Badge>
                </div>
              </CardContent>
            </Card>

            {/* Upcoming Calendar */}
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">Upcoming</CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => navigate('/calendar')}>
                    <ExternalLink className="h-3 w-3" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {upcomingEvents && upcomingEvents.length > 0 ? (
                  <div className="space-y-3">
                    {upcomingEvents.slice(0, 5).map((event: any) => (
                      <div
                        key={event.id}
                        className="flex items-start gap-3 p-2 rounded-md hover:bg-muted/50 cursor-pointer"
                        onClick={() => navigate(`/calendar?eventId=${event.id}`)}
                      >
                        <div className="flex flex-col items-center min-w-[40px] text-center">
                          <span className="text-xs text-muted-foreground uppercase">
                            {format(new Date(event.start_time), 'MMM')}
                          </span>
                          <span className="text-lg font-bold">
                            {format(new Date(event.start_time), 'd')}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium line-clamp-1">{event.title}</p>
                          <p className="text-xs text-muted-foreground">
                            {format(new Date(event.start_time), 'h:mm a')}
                            {event.location && ` · ${event.location}`}
                            {event.count > 1 && ` · ${event.count} bookings`}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No upcoming events
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => navigate('/pipeline')}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create New Lead
                </Button>
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => navigate('/operations/coffeebeans')}
                >
                  <Coffee className="h-4 w-4 mr-2" />
                  View Bookings
                </Button>
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => navigate('/calendar')}
                >
                  <CalendarDays className="h-4 w-4 mr-2" />
                  Open Calendar
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

function getActivityIcon(type: ActivityItem['type']) {
  switch (type) {
    case 'booking':
      return <Coffee className="h-4 w-4 text-amber-600" />;
    case 'notification':
      return <Bell className="h-4 w-4 text-blue-600" />;
    case 'message':
      return <MessageSquare className="h-4 w-4 text-purple-600" />;
    case 'calendar':
      return <CalendarDays className="h-4 w-4 text-green-600" />;
    default:
      return <Clock className="h-4 w-4 text-gray-600" />;
  }
}

function getActivityIconBg(type: ActivityItem['type']): string {
  switch (type) {
    case 'booking':
      return 'bg-amber-100 dark:bg-amber-900/30';
    case 'notification':
      return 'bg-blue-100 dark:bg-blue-900/30';
    case 'message':
      return 'bg-purple-100 dark:bg-purple-900/30';
    case 'calendar':
      return 'bg-green-100 dark:bg-green-900/30';
    default:
      return 'bg-gray-100 dark:bg-gray-900/30';
  }
}

export default Dashboard;
